import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  CheckboxGroup,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import useSWRMutation from 'swr/mutation'
import { useUser } from 'src/context';
import { postVote } from 'src/api';
import { secondRoundCandidates } from './static';

const VoteDialog = ({ isOpen, onClose, sectionKey, onSubmitted }) => {
  const category = secondRoundCandidates[sectionKey];
  
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  const { isLogin, isLoadingUser, user } = useUser();
  const toast = useToast();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      checkedItems: [],
    },
  });

  const checkedItems = watch('checkedItems', []);
  
  const hasContent = checkedItems.length > 0;

  const { trigger, isMutating } = useSWRMutation(
    isLogin ? `vote-${sectionKey}-${user?.customId}` : null, 
    (url, { arg }) => postVote(arg),
    {
      onSuccess: (res) => {
        setShowConfirmDialog(false);
        setPendingData(null);
        onClose();

        if (res.status === 200) {
          toast({
            title: '投票成功',
            description: '您的投票已成功送出',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });

          onSubmitted?.();
        } else {
          const errorMessage = res.data?.result || '請稍候再試';
          toast({
            title: '投票失敗',
            description: errorMessage,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      },
      onError: (err) => {
        setShowConfirmDialog(false);
        toast({
          title: '投票失敗',
          description: err.data?.result || '發生未預期的錯誤，請稍後再試',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  );

  useEffect(() => {
    if (isOpen) {
      reset({
        checkedItems: [],
      });
    }
  }, [isOpen, sectionKey, reset]);

  const onSubmit = (data) => {
    if (!isLogin || isLoadingUser) {
      setError('root.serverError', { type: 'custom', message: '請先登入' });
      return;
    }

    const { checkedItems } = data;
    if (checkedItems.length === 0) {
      setError('root.serverError', { type: 'custom', message: '請至少選擇一個項目' });
      return;
    }

    if (checkedItems.length > category.limit) {
      setError('root.serverError', { type: 'custom', message: `選擇數量超過上限，此項目最多只能選擇 ${category.limit} 個。` });
      return;
    }

    const payload = {
      section: sectionKey,
      poll: checkedItems
    };

    setPendingData(payload);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (pendingData) {
      await trigger(pendingData);
    }
  };

  const handleCloseAttempt = () => {
    if (hasContent) {
      setShowCloseConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirmDialog(false);
    reset();
    onClose();
  };

  if (!category) {
    return null;
  }

  const canAddMore = checkedItems.length < category.limit;
  const isRookies = sectionKey === 'rookies';

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCloseAttempt} size="xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>{category.name}</ModalHeader>
          <ModalCloseButton onClick={handleCloseAttempt} />
          <ModalBody>
            <p className="mb-4 font-bold">{category.description}</p>
            <p className="font-bold mb-2">
              此項目可投 {category.limit} 票 (目前已選 {checkedItems.length} {isRookies ? '間' : '款'})
            </p>
            {errors.root?.serverError && (
              <Text color="red.500" mt={2}>{errors.root.serverError.message}</Text>
            )}

            <FormControl isInvalid={!!errors.checkedItems}>
              <Controller
                name="checkedItems"
                control={control}
                render={({ field }) => (
                  <CheckboxGroup {...field}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                      {category.candidates.map((candidate) => (
                        <Checkbox
                          key={candidate.id}
                          value={candidate.id}
                          isDisabled={!field.value.includes(candidate.id) && !canAddMore}
                        >
                          {candidate.title}
                        </Checkbox>
                      ))}
                    </div>
                  </CheckboxGroup>
                )}
              />
              <FormErrorMessage>{errors.checkedItems?.message}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseAttempt}>
              取消
            </Button>
            <Button colorScheme="blue" type="submit" isLoading={isSubmitting || isMutating}>
              投票
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              確認投票
            </AlertDialogHeader>
            <AlertDialogBody>
              送出後將無法修改，確認送出？
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={() => setShowConfirmDialog(false)} isDisabled={isMutating}>
                取消
              </Button>
              <Button colorScheme="blue" onClick={handleConfirmSubmit} ml={3} isLoading={isMutating}>
                確認送出
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={showCloseConfirmDialog} onClose={() => setShowCloseConfirmDialog(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              確認關閉
            </AlertDialogHeader>
            <AlertDialogBody>
              您有未送出的內容，確定要關閉視窗嗎？關閉後內容將會遺失。
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={() => setShowCloseConfirmDialog(false)}>
                取消
              </Button>
              <Button colorScheme="red" onClick={handleConfirmClose} ml={3}>
                確定關閉
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default VoteDialog;
